import { useMemo, useState } from 'react';
import AdminNav from '@/components/admin/AdminNav';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { allRoutesComplete } from '@/lib/route-mapping';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Download, Search, ExternalLink, Globe } from 'lucide-react';
import { LocalizedLink } from '@/components/LocalizedLink';

const siteUrl = 'https://danielalbertlaw.com';

type RouteMap = Record<string, { en: string; es: string; vi: string }>

interface RouteGroup {
  category: string;
  routes: Array<{ key: string; en: string; es: string; vi: string }>;
}

const SiteMap = () => {
  const [searchTerm, setSearchTerm] = useState('');

  const groupedRoutes = useMemo(() => {
    const entries = Object.entries(allRoutesComplete as RouteMap);
    const groups: Record<string, RouteGroup> = {
      general: { category: 'General Pages', routes: [] },
      practiceAreas: { category: 'Practice Areas', routes: [] },
      areas: { category: 'Houston Areas', routes: [] },
      blog: { category: 'Blog Posts', routes: [] },
      immigration: { category: 'Immigration Services', routes: [] },
      criminal: { category: 'Criminal Defense', routes: [] },
      family: { category: 'Family Law', routes: [] },
      personalInjury: { category: 'Personal Injury', routes: [] },
      realEstate: { category: 'Real Estate', routes: [] },
      business: { category: 'Business Law', routes: [] },
      probate: { category: 'Probate & Estate', routes: [] },
      tax: { category: 'Tax Law', routes: [] },
      other: { category: 'Other Pages', routes: [] },
    };

    entries.forEach(([key, value]) => {
      const route = {
        key,
        en: value.en,
        es: value.es,
        vi: value.vi,
      };

      // Categorize routes
      if (['home', 'about', 'contact', 'consultation', 'booking', 'practiceAreas'].includes(key)) {
        groups.general.routes.push(route);
      } else if (value.en.startsWith('/areas/')) {
        groups.areas.routes.push(route);
      } else if (value.en.startsWith('/blog/')) {
        groups.blog.routes.push(route);
      } else if (key.includes('immigration') || key.includes('asylum') || key.includes('citizenship') || 
                 key.includes('deportation') || key.includes('visa') || key.includes('daca') || 
                 key.includes('vawa') || key.includes('greenCard') || key.includes('consular') ||
                 key.includes('eb5') || key.includes('student')) {
        groups.immigration.routes.push(route);
      } else if (key.includes('criminal') || key.includes('dwi') || key.includes('drug') || 
                 key.includes('assault') || key.includes('violent') || key.includes('sex') ||
                 key.includes('theft') || key.includes('expunction')) {
        groups.criminal.routes.push(route);
      } else if (key.includes('family') || key.includes('divorce') || key.includes('custody') ||
                 key.includes('cps') || key.includes('adoption') || key.includes('support')) {
        groups.family.routes.push(route);
      } else if (key.includes('personalInjury') || key.includes('accident') || key.includes('injury') ||
                 key.includes('malpractice') || key.includes('industrial')) {
        groups.personalInjury.routes.push(route);
      } else if (key.includes('realEstate') || key.includes('foreclosure') || key.includes('title') ||
                 key.includes('closing') || key.includes('buying') || key.includes('landlord') ||
                 key.includes('lease') || key.includes('affidavit') || key.includes('lien')) {
        groups.realEstate.routes.push(route);
      } else if (key.includes('business') || key.includes('contract') || key.includes('debt') ||
                 key.includes('formation') || key.includes('intellectual') || key.includes('partnership') ||
                 key.includes('trademark') || key.includes('nonprofit') || key.includes('registered') ||
                 key.includes('insurance') || key.includes('transaction')) {
        groups.business.routes.push(route);
      } else if (key.includes('probate') || key.includes('estate') || key.includes('guardianship') ||
                 key.includes('trust')) {
        groups.probate.routes.push(route);
      } else if (key.includes('tax')) {
        groups.tax.routes.push(route);
      } else if (key.includes('tools') || key.includes('privacy') || key.includes('terms')) {
        groups.other.routes.push(route);
      } else {
        groups.practiceAreas.routes.push(route);
      }
    });

    // Filter based on search
    if (searchTerm) {
      Object.keys(groups).forEach((groupKey) => {
        groups[groupKey].routes = groups[groupKey].routes.filter(
          (route) =>
            route.key.toLowerCase().includes(searchTerm.toLowerCase()) ||
            route.en.toLowerCase().includes(searchTerm.toLowerCase()) ||
            route.es.toLowerCase().includes(searchTerm.toLowerCase()) ||
            route.vi.toLowerCase().includes(searchTerm.toLowerCase())
        );
      });
    }

    return Object.values(groups).filter(group => group.routes.length > 0);
  }, [searchTerm]);

  const totalRoutes = useMemo(() => {
    return groupedRoutes.reduce((acc, group) => acc + group.routes.length, 0);
  }, [groupedRoutes]);

  const exportSitemap = () => {
    const lines: string[] = [
      '<?xml version="1.0" encoding="UTF-8"?>',
      '<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9" xmlns:xhtml="http://www.w3.org/1999/xhtml">',
    ];

    groupedRoutes.forEach(group => {
      group.routes.forEach(route => {
        lines.push('  <url>');
        lines.push(`    <loc>${siteUrl}${route.en}</loc>`);
        lines.push(`    <xhtml:link rel="alternate" hreflang="en" href="${siteUrl}${route.en}"/>`);
        lines.push(`    <xhtml:link rel="alternate" hreflang="es" href="${siteUrl}${route.es}"/>`);
        lines.push(`    <xhtml:link rel="alternate" hreflang="vi" href="${siteUrl}${route.vi}"/>`);
        lines.push(`    <xhtml:link rel="alternate" hreflang="x-default" href="${siteUrl}${route.en}"/>`);
        lines.push('    <changefreq>weekly</changefreq>');
        lines.push('    <priority>0.8</priority>');
        lines.push('  </url>');
      });
    });

    lines.push('</urlset>');

    const blob = new Blob([lines.join('\n')], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'sitemap.xml';
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <SEO title="Mapa del Sitio (Admin)" description="Visualización completa del mapa del sitio con todas las URLs" noindex />
      <div className="min-h-screen flex flex-col bg-background">
        <AdminNav />
        <main className="flex-1 py-8">
          <div className="container mx-auto px-4">
            <Card>
              <CardHeader>
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Globe className="h-6 w-6" />
                      Mapa del Sitio Web
                    </CardTitle>
                    <CardDescription>
                      {totalRoutes} páginas organizadas por categoría • 3 idiomas (EN/ES/VI)
                    </CardDescription>
                  </div>
                  <Button onClick={exportSitemap}>
                    <Download className="h-4 w-4 mr-2" />
                    Exportar Sitemap XML
                  </Button>
                </div>
                <div className="relative mt-4">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Buscar páginas..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </CardHeader>
              <CardContent className="space-y-8">
                {groupedRoutes.map((group) => (
                  <div key={group.category} className="space-y-4">
                    <div className="flex items-center gap-2">
                      <h3 className="text-xl font-semibold">{group.category}</h3>
                      <Badge variant="secondary">{group.routes.length}</Badge>
                    </div>
                    <div className="grid gap-4">
                      {group.routes.map((route) => (
                        <Card key={route.key} className="p-4">
                          <div className="space-y-3">
                            <div className="flex items-start justify-between gap-2">
                              <div>
                                <code className="text-sm font-mono bg-muted px-2 py-1 rounded">
                                  {route.key}
                                </code>
                              </div>
                            </div>
                            <div className="grid gap-2">
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className="w-8 justify-center">EN</Badge>
                                <LocalizedLink to={route.en} className="flex-1 text-sm hover:underline flex items-center gap-1 group">
                                  {route.en}
                                  <ExternalLink className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                                </LocalizedLink>
                              </div>
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className="w-8 justify-center">ES</Badge>
                                <LocalizedLink to={route.es} className="flex-1 text-sm hover:underline flex items-center gap-1 group">
                                  {route.es}
                                  <ExternalLink className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                                </LocalizedLink>
                              </div>
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className="w-8 justify-center">VI</Badge>
                                <LocalizedLink to={route.vi} className="flex-1 text-sm hover:underline flex items-center gap-1 group">
                                  {route.vi}
                                  <ExternalLink className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                                </LocalizedLink>
                              </div>
                            </div>
                          </div>
                        </Card>
                      ))}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default SiteMap;
